import { useParams, useLocation } from "react-router-dom";
import styled from "styled-components";
import { InventoryHeader } from "./InventoryHeader";
import { InventoryOptions } from "./InventoryOptions";
import { InventoryTable } from "./InventoryTable";
import { useGetProducts, useGetMergedProducts } from "../../hooks/productHooks";
import {
    useGetInventory,
    useGetMergedInventory,
    useGetAllCompanyInventories,
    useGetAllInventoriesByIds,
} from "../../hooks/inventoryHooks";
import { useGetCompanies, useGetCompany, useGetCompaniesByIds } from "../../hooks/companyHooks";
import { Loader } from "../../components/Loader";
import { useEffect, useState } from "react";
import { Product } from "../../types";
import { GridRowId } from "@mui/x-data-grid";
import { Modal } from "../../components/Modals/Modal";
import { PrintModal } from "../../components/Modals/PrintModal";
import { useModal } from "../../hooks/useModal";
import { useGetProductColors } from "../../hooks/productColorHooks";
import { useGetActions } from "../../hooks/actionHooks";
import { useGetProductStatuses } from "../../hooks/productStatusHooks";
import { useGetProductCategories } from "../../hooks/productCategoryHooks";
import { useGetDesigners } from "../../hooks/designerHooks";
import { useGetManufacturers } from "../../hooks/manufacturerHooks";
import { useGetMaterials } from "../../hooks/materialHooks";
import { Inventory, MergedInventory } from "../../types";
import React from "react";

type InventoryData = Inventory | MergedInventory;

export const InventoryPage = () => {
    const { companyId, inventoryId } = useParams();
    type InventoryLocationState = {
        isMerged?: boolean;
    };

    const location = useLocation();
    const state = location.state as InventoryLocationState | undefined;
    const isMerged = state?.isMerged ?? false;

    const inventoryQuery = (
        isMerged
            ? useGetMergedInventory(companyId ?? "", inventoryId ?? "")
            : useGetInventory(companyId ?? "", inventoryId ?? "")
    ) as {
        data?: InventoryData;
        isLoading: boolean;
        isError: boolean;
    };

    const productQuery = isMerged
        ? useGetMergedProducts(inventoryId ?? "")
        : useGetProducts(companyId ?? "", inventoryId ?? "");

    const mergedData: InventoryData | undefined = Array.isArray(inventoryQuery.data)
        ? (inventoryQuery.data as InventoryData)
        : (inventoryQuery.data as InventoryData);

    const inventoryData = inventoryQuery.data;
    let currentInventory: InventoryData | undefined;

    if (Array.isArray(inventoryData)) {
        currentInventory = (inventoryData as InventoryData[]).find(
            (inv: InventoryData) => inv.id === inventoryId,
        );
    } else {
        currentInventory = inventoryData;
    }

    const [uniqueCompanyIds, setUniqueCompanyIds] = useState<string[]>([]);
    const [uniqueInventoryIds, setUniqueInventoryIds] = useState<string[]>([]);

    useEffect(() => {
        const ucid = Array.from(
            new Set<string>(productQuery.data?.map((product: Product) => product.companyId) ?? []),
        );
        const uiid = Array.from(
            new Set<string>(
                productQuery.data?.map((product: Product) => product.inventoryId) ?? [],
            ),
        );
        setUniqueCompanyIds(ucid);
        setUniqueInventoryIds(uiid);
    }, [productQuery.data]);

    const { data: productColors } = useGetProductColors();
    const { data: actionData } = useGetActions();
    const { data: productStatusData } = useGetProductStatuses();
    const { data: productCategories } = useGetProductCategories();
    const { data: productDesigners } = useGetDesigners();
    const { data: productManufacturers } = useGetManufacturers();
    const { data: productMaterials } = useGetMaterials();
    const { data: companies } = useGetCompaniesByIds(uniqueCompanyIds);
    const { data: inventories } = useGetAllInventoriesByIds(uniqueInventoryIds, uniqueCompanyIds);

    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [printUrl, setPrintUrl] = useState<string | null>(null);

    const { isShown, toggle } = useModal();

    const onConfirm = () => toggle();
    const onCancel = () => toggle();

    const handleRowClick = (id: GridRowId) => {
        const products = productQuery.data;

        if (!products || !companyId || !inventoryId) return;

        const product = products.find((product: Product) => product.id === id);
        if (!product) return;
        setSelectedProduct(product);

        const url = `/companies/${String(product.companyId)}/inventories/${String(
            product.inventoryId,
        )}/products/${String(product.id)}`;
        setPrintUrl(url);
    };

    return (
        <>
            {selectedProduct && printUrl && (
                <Modal
                    isShown={isShown}
                    hide={toggle}
                    headerText={"Print QR code for product"}
                    textColor="#8c8c8c"
                    modalContent={
                        <PrintModal
                            onConfirm={onConfirm}
                            onCancel={onCancel}
                            product={selectedProduct}
                            printUrl={printUrl}
                        />
                    }
                />
            )}

            <InventoryOverviewPageWrapper>
                {inventoryQuery.isLoading || inventoryQuery.data == undefined ? (
                    <Loader />
                ) : inventoryQuery.isError ? (
                    <p>Error</p>
                ) : (
                    <InventoryHeader
                        heading={currentInventory?.name ?? ""}
                        subheading={currentInventory?.estimatedDeliveryDate?.slice(0, 10) ?? ""}
                    />
                )}

                {productQuery.isLoading || productQuery.data == undefined ? (
                    <Loader />
                ) : productQuery.isError ? (
                    <p>Error</p>
                ) : (
                    <>
                        <InventoryTable
                            merged={isMerged}
                            products={
                                productQuery.data
                                    ? [...productQuery.data].sort((a, b) =>
                                          (a.name ?? "").localeCompare(b.name ?? ""),
                                      )
                                    : []
                            }
                            companyId={companyId ?? ""}
                            inventoryId={inventoryId ?? ""}
                            handleRowClick={handleRowClick}
                            inventoryName={currentInventory?.name ?? ""}
                            inventorySerialNumber={
                                !isMerged && "serialNumber" in (inventoryQuery.data ?? {})
                                    ? Number((inventoryQuery.data as Inventory)?.serialNumber ?? 0)
                                    : 0
                            }
                            productColors={productColors ?? []}
                            productActions={actionData ?? []}
                            productStatuses={productStatusData ?? []}
                            productCategories={productCategories ?? []}
                            productDesigners={productDesigners ?? []}
                            productManufacturers={productManufacturers ?? []}
                            productMaterials={productMaterials ?? []}
                            inventories={inventories ?? []}
                            companies={companies ?? []}
                        />

                        <InventoryOptions
                            companyId={companyId ?? ""}
                            inventoryId={inventoryId ?? ""}
                            handlePrint={toggle}
                            merged={isMerged}
                        />
                    </>
                )}
            </InventoryOverviewPageWrapper>
        </>
    );
};

const InventoryOverviewPageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: calc(100vh + 15vw);
    max-height: 85vh;
    gap: 10px;
`;
